import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import footerLogo from '../assets/shared/icons/neoLogo.svg';
import backToTopSvg from '../assets/Link.svg';
import homeFacebookIcon from '../assets/facebook.svg';
import homeInstagramIcon from '../assets/instagram.svg';
import homeLinkedinIcon from '../assets/linkedin.svg';
import SectionContainer from './SectionContainer';
import './HomeContactFooter.css';

const ADDRESS = 'Bay square building 12, office #304, Business Bay, Dubai';
const FOOTER_TAGLINE = 'Thoughtful design. Exceptional quality. Modern living in Dubai.';
const FACEBOOK_URL = 'https://www.facebook.com/';
const INSTAGRAM_URL = 'https://www.instagram.com/neoterra_developments';
const LINKEDIN_URL = 'https://www.linkedin.com/company/neoterra-real-estate-development-l-l-c/';

const FOOTER_NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Our story', to: '/our-story' },
  { label: 'careers', to: '/careers' },
  { label: 'Media Center', to: '/media-centre' },
  { label: 'Press Release', to: '/press-release' },
];

const FOOTER_PROJECT_LINKS = [
  { label: 'Eira', to: '/projects/eira' },
  { label: 'Elmora', to: '/projects/elmora' },
];

const FOOTER_NAV_LINKS_MOBILE = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/our-story' },
  { label: 'careers', to: '/careers' },
  { label: 'Media Center', to: '/media-centre' },
  { label: 'Press release', to: '/press-release' },
];

const FOOTER_PROJECT_LINKS_MOBILE = [
  { label: 'Elmora', to: '/projects/elmora' },
  { label: 'Eira', to: '/projects/eira' },
];

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const backToTopBtnClass =
  'home-back-to-top-btn inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 transition hover:opacity-80';

const BackToTopIcon = () => (
  <img
    src={backToTopSvg}
    alt=""
    aria-hidden
    className="block h-full w-full object-contain"
    width="45"
    height="45"
  />
);

/**
 * @param {'home' | 'default'} variant
 * - home: dark footer + sticky back-to-top (landing only)
 * - default: previous light footer, no sticky arrow
 */
const HomeContactFooter = ({
  contained = false,
  sectionClassName = '',
  sectionId = undefined,
  variant = 'default',
}) => {
  const isHome = variant === 'home';
  const [showStickyBackToTop, setShowStickyBackToTop] = useState(false);
  const footerRef = useRef(null);
  const [stickyStyle, setStickyStyle] = useState({ right: 20, bottom: 24 });

  useEffect(() => {
    if (!isHome) return undefined;

    const syncStickyFromFooter = () => {
      const isLg = window.matchMedia('(min-width: 1024px)').matches;
      const isMd = window.matchMedia('(min-width: 768px)').matches;
      const bottomInset = isLg ? 40 : isMd ? 32 : 24;
      const rightInset = isLg ? 48 : isMd ? 32 : 20;
      setStickyStyle({
        right: rightInset,
        bottom: bottomInset,
      });
    };

    const updateVisibility = () => {
      syncStickyFromFooter();
      setShowStickyBackToTop(window.scrollY >= Math.max(window.innerHeight * 0.85, 1));
    };

    updateVisibility();
    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    return () => {
      window.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [isHome]);

  const outerSectionClass = isHome
    ? `home-contact-final home-contact-final--home relative z-[3] w-full border-t-[1.03px] border-solid border-[#BC863933] bg-[#FAFAFA99] lg:border-t-0 lg:bg-[#FFFFFF] ${sectionClassName}`.trim()
    : `home-contact-final relative z-[3] w-full border-t-[1.03px] border-solid border-[#BC863933] bg-[#FAFAFA99] text-[#1f1f1f] lg:border-t-0 lg:bg-[#FFFFFF] ${sectionClassName}`.trim();

  const stickyBackToTop =
    isHome && showStickyBackToTop && typeof document !== 'undefined'
      ? createPortal(
        <button
          type="button"
          onClick={scrollToTop}
          aria-label="Back to top"
          className={`home-back-to-top fixed z-[200] ${backToTopBtnClass}`}
          style={{ right: stickyStyle.right, bottom: stickyStyle.bottom }}
        >
          <BackToTopIcon />
        </button>,
        document.body,
      )
      : null;

  const renderSocialLinks = () => (
    <>
      <a
        className="site-footer-desktop__social-link home-footer-social home-footer-social--instagram"
        href={INSTAGRAM_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Instagram"
      >
        <img className="home-footer-social-icon block" src={homeInstagramIcon} alt="" width="27" height="27" />
      </a>
      <a
        className="site-footer-desktop__social-link home-footer-social"
        href={FACEBOOK_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Facebook"
      >
        <img className="home-footer-social-icon block" src={homeFacebookIcon} alt="" width="32" height="32" />
      </a>
      <a
        className="site-footer-desktop__social-link home-footer-social"
        href={LINKEDIN_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="LinkedIn"
      >
        <img className="home-footer-social-icon block" src={homeLinkedinIcon} alt="" width="32" height="32" />
      </a>
    </>
  );

  const mobileBlock = (
    <div className="site-footer-mobile lg:hidden">
      <div className="site-footer-mobile__inner">
        <Link to="/" className="site-footer-mobile__logo-link" aria-label="Neoterra home">
          <img
            src={footerLogo}
            alt="Neoterra Developments"
            className="site-footer-mobile__logo"
            width="91"
            height="57"
          />
        </Link>
        <p className="site-footer-mobile__tagline">{FOOTER_TAGLINE}</p>

        <div className="site-footer-mobile__column">
          <h2 className="site-footer-mobile__heading">Navigate</h2>
          <nav className="site-footer-mobile__nav" aria-label="Footer navigation">
            {FOOTER_NAV_LINKS_MOBILE.map((item) => (
              <Link key={item.to} className="site-footer-mobile__link" to={item.to}>
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="site-footer-mobile__column">
          <h2 className="site-footer-mobile__heading">Projects</h2>
          <nav className="site-footer-mobile__nav" aria-label="Footer projects">
            {FOOTER_PROJECT_LINKS_MOBILE.map((item) => (
              <Link key={item.to} className="site-footer-mobile__link" to={item.to}>
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="site-footer-mobile__column">
          <h2 className="site-footer-mobile__heading">Contact</h2>
          <div className="site-footer-mobile__contact">
            <a href="tel:80063683772" className="site-footer-mobile__link">
              800 NEOTERRA
            </a>
            <a href="mailto:contact@neoterra.ae" className="site-footer-mobile__link site-footer-mobile__link--email">
              contact@neoterra.ae
            </a>
            <p className="site-footer-mobile__address">{ADDRESS}</p>
          </div>
        </div>

        <div className="site-footer-mobile__rule" role="presentation" aria-hidden="true" />

        <p className="site-footer-mobile__copyright">
          © 2026 Neoterra. All rights reserved. Crafted with excellence.
        </p>

        <div className="site-footer-mobile__social" aria-label="Social links">
          {renderSocialLinks()}
        </div>
      </div>
    </div>
  );

  const desktopBlock = (
    <div className="site-footer-desktop hidden w-full lg:block">
      <div className="site-footer-desktop__main">
        <div className="site-footer-desktop__grid">
          <div className="site-footer-desktop__brand">
            <Link to="/" className="site-footer-desktop__logo-link" aria-label="Neoterra home">
              <img
                src={footerLogo}
                alt="Neoterra Developments"
                className="site-footer-desktop__logo"
                width="91"
                height="57"
              />
            </Link>
            <p className="site-footer-desktop__tagline">{FOOTER_TAGLINE}</p>
          </div>

          <div className="site-footer-desktop__column">
            <h2 className="site-footer-desktop__heading">Navigate</h2>
            <nav className="site-footer-desktop__nav" aria-label="Footer navigation">
              {FOOTER_NAV_LINKS.map((item) => (
                <Link key={item.to} className="site-footer-desktop__link" to={item.to}>
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="site-footer-desktop__column">
            <h2 className="site-footer-desktop__heading">Projects</h2>
            <nav className="site-footer-desktop__nav" aria-label="Footer projects">
              {FOOTER_PROJECT_LINKS.map((item) => (
                <Link key={item.to} className="site-footer-desktop__link" to={item.to}>
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="site-footer-desktop__column">
            <h2 className="site-footer-desktop__heading">Contact</h2>
            <div className="site-footer-desktop__contact">
              <a href="tel:80063683772" className="site-footer-desktop__contact-line site-footer-desktop__link">
                800 NEOTERRA
              </a>
              <a
                href="mailto:contact@neoterra.ae"
                className="site-footer-desktop__contact-line site-footer-desktop__link site-footer-desktop__link--email"
              >
                contact@neoterra.ae
              </a>
              <p className="site-footer-desktop__contact-line site-footer-desktop__address">{ADDRESS}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="site-footer-desktop__bar">
        <p className="site-footer-desktop__copyright">
          © 2026 Neoterra. All rights reserved. Crafted with excellence.
        </p>
        <div className="site-footer-desktop__social" aria-label="Social links">
          {renderSocialLinks()}
        </div>
      </div>
    </div>
  );

  const mainColumns = (
    <>
      {mobileBlock}
      {desktopBlock}
    </>
  );

  if (contained) {
    return (
      <>
        {stickyBackToTop}
        <SectionContainer
          as="section"
          className={outerSectionClass}
          id={sectionId}
          aria-label="Contact us"
          maxWidthClass="w-full max-w-none"
          ref={footerRef}
        >
          {mainColumns}
        </SectionContainer>
      </>
    );
  }

  return (
    <>
      {stickyBackToTop}
      <section ref={footerRef} className={outerSectionClass} id={sectionId} aria-label="Contact us">
        {mainColumns}
      </section>
    </>
  );
};

export default HomeContactFooter;
